import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { FilterSelect, ItemTypeahead, ServiceThreadSpinner } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, UserContext } from "../utils";
import _ from "lodash";

export default function ItemSpliceReqs() {
   const { currentUser } = useContext(UserContext);
   const ALL_OPTION = { value: 0, label: "-- Select --" };
   const ALL_SPLICE_OPT = { value: 0, label: "-- Select --", hasOsc: false };
   const [filters, setFilters] = useState([
      { filterName: "thdSplicer", value: ALL_OPTION },
      { filterName: "airSplicer", value: ALL_OPTION },
      { filterName: "chamber", value: ALL_OPTION },
      { filterName: "oscillator", value: ALL_OPTION },
      { filterName: "thread", value: ALL_OPTION },
   ]);
   const [airSplicers, setAirSplicers] = useState([]);  // list of air splicers, based on yarn join type
   const [chambers, setChambers] = useState([]);        // chambers filtered by selected air splicer
   const [chamList, setChamList] = useState([]);        // chambers unfiltered
   const [data, setData] = useState(null);              // Item with Splice Requirements
   const initialSelect = { label: "--- Select ---", value: "--- Select ---" };
   const [loaded, setLoaded] = useState(true);
   const [noEdit, setDisabled] = useState(true);
   const [oscillators, setOscillators] = useState([]);  // oscillators filtered by selected air splicer
   const [oscList, setOscList] = useState([]);          // oscillators unfiltered
   const [selectedItem, setSelectedItem] = useState(initialSelect);
   const [showOsc, setShowOsc] = useState(false);       // show oscillator list only when a selected air splicer uses them
   const [thdSplicers, setThdSplicers] = useState([]);  // list of thread splicers, based on yarn join type
   const [threadList, setThreadList] = useState([]);    // thread type options unfiltered
   const [threads, setThreads] = useState([]);          // thread types by selected thread splicer
   const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

   useEffect(() => {
      if (!currentUser) return;

      // Get possible drop-down options
      api
         .fetch(`MachineSetup/GetSpliceOptions`)
         .then((response) => {
            if (response.data.success) {
               setDisabled(!response.data.canEdit);
               const airList = _.concat(
                  [ALL_SPLICE_OPT],
                  _.map(response.data.opts.airSplicers, (a) => ({
                     value: a.id,
                     label: a.splicer,
                     hasOsc: a.hasOscillator,
                  }))
               );
               const thdList = _.concat(
                  [ALL_SPLICE_OPT],
                  _.map(response.data.opts.threadSplicers, (t) => ({
                     value: t.id,
                     label: t.splicer,
                     hasOsc: false,
                  }))
               );
               const chamberList = _.concat(
                  _.map(response.data.opts.chambers, (c) => ({
                     value: c.chamberId,
                     label: c.chamber,
                     parent: c.splicerId,
                  }))
               );
               const oscList = _.concat(
                  _.map(response.data.opts.oscillators, (o) => ({
                     value: o.oscillatorId,
                     label: o.oscillator,
                     parent: o.splicerId,
                  }))
               );
               const threadList = _.concat(
                  _.map(response.data.opts.threads, (t) => ({
                     value: t.threadId,
                     label: t.thread,
                     parent: t.splicerId,
                  }))
               );
               return {
                  airList: airList,
                  thdList: thdList,
                  chamberList: chamberList,
                  oscList: oscList,
                  threadList: threadList,
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ airList, thdList, chamberList, oscList, threadList }) => {
            setAirSplicers(airList);
            setThdSplicers(thdList);
            setChamList(chamberList);
            setOscList(oscList);
            setThreadList(threadList);

            // These should only fill in after a splicer is selected, just set a default option
            setChambers([ALL_OPTION]);
            setOscillators([ALL_OPTION]);
            setThreads([ALL_OPTION]);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   useEffect(() => {
      async function setFields() {
         await sleep(50); // allows time for input to be recognized
         if (!loaded && data) {
            // populate drop-downs as appropriate for the selected item
            if (data.isAir === true) {
               let asId = (data.splicerId > 0) ? data.splicerId : 0;
               const asplice = _.find(airSplicers, (x) => x.value === asId);
               let tmpChamList = await fixChambers(asId);
               let tmpOscList = await fixOscillators(asId);

               // set chamber filter
               let cNum = (data.chamberId > 0) ? data.chamberId : 0;
               const cFilt = _.find(tmpChamList, (s) => s.value === cNum);

               // set oscillator filter
               let oNum = (data.oscillatorId > 0) ? data.oscillatorId : 0;
               const oFilt = _.find(tmpOscList, (s) => s.value === oNum);

               // set selected DDL values
               let newFilt = filterHelpers.getFilters(filters, {
                  filterName: "airSplicer",
                  value: asplice,
               });
               newFilt = filterHelpers.getFilters(newFilt, {
                  filterName: "chamber",
                  value: cFilt,
               });
               newFilt = filterHelpers.getFilters(newFilt, {
                  filterName: "oscillator",
                  value: oFilt,
               });
               setFilters(newFilt);

               // if this splicer uses oscillators then show them
               setShowOsc(asplice.hasOsc);
            }
            else if (data.isThread === true) {
               let thId = (data.splicerId > 0) ? data.splicerId : 0;
               const tsplice = _.find(thdSplicers, (x) => x.value === thId);
               let tmpThreadList = await fixThreads(thId);

               // set the thread filter
               let num = (data.threadId > 0) ? data.threadId : 0;
               const tmpThdFilter = _.find(tmpThreadList, (t) => t.value === num);

               // set selected DDL values
               let newFilters = filterHelpers.getFilters(filters, {
                  filterName: "thdSplicer",
                  value: tsplice,
               });
               newFilters = filterHelpers.getFilters(newFilters, {
                  filterName: "thread",
                  value: tmpThdFilter,
               });
               setFilters(newFilters);
            }
            else {
               // this item does not need splice information
            }
            setLoaded(true);
         }
      }
      setFields();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data]);

   useEffect(() => {
      if (selectedItem === null || selectedItem === initialSelect)
         return;
      fetchItem();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedItem]);

   function fetchItem() {
      // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
      var encodedItem = selectedItem.value.replace(/\//g, '%2F').replace(/#/g, '%23');
      setLoaded(false);
      api
         .post(`MachineSetup/GetSpliceItem/${encodedItem.toUpperCase()}`)
         .then(response => {
            if (response.data.success) {
               setData(response.data.item);
            }
            else {
               setData({});
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            setData({});
            api.catchHandler(error);
         })
         .finally(() => {
            //setSelectedItem(initialSelect);
         });
   }

   const fixChambers = async (id) => {
      let newChams = [ALL_OPTION];
      var subs = _.pickBy(chamList, (e) => _.includes([id], e.parent));
      if (subs) { // There are chamber options for the selected air splicer
         newChams = _.concat(
            [ALL_OPTION],
            _.map(subs, (s) => ({ value: s.value, label: s.label }))
         );
         setChambers(newChams);
      }
      return newChams;
   };

   const fixOscillators = async (id) => {
      let newOsc = [ALL_OPTION];
      var subs = _.pickBy(oscList, (e) => _.includes([id], e.parent));
      if (subs) { // There are oscillator options for the selected air splicer
         newOsc = _.concat(
            [ALL_OPTION],
            _.map(subs, (s) => ({ value: s.value, label: s.label }))
         );
         setOscillators(newOsc);
      }
      return newOsc;
   };

   const fixThreads = async (id) => {
      let newThdList = [ALL_OPTION];
      var subs = _.pickBy(threadList, (e) => _.includes([id], e.parent));
      if (subs) { // There are thread options for the selected thread splicer
         newThdList = _.concat(
            [ALL_OPTION],
            _.map(subs, (s) => ({ value: s.value, label: s.label }))
         );
         setThreads(newThdList);
      }
      return newThdList;
   };

   const onAirSpliceChange = async (newFilterValue) => {
      // fill in the air splicer drop-downs with proper values
      const splicer = _.find(airSplicers, (x) => x.value === newFilterValue.value);
      let newChamList = await fixChambers(newFilterValue.value);
      let newOscList = await fixOscillators(newFilterValue.value);

      // set the related filters
      const cFilter = _.find(newChamList, (s) => s.value === 0);
      const oFilter = _.find(newOscList, (s) => s.value === 0);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "airSplicer",
         value: splicer,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "chamber",
         value: cFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "oscillator",
         value: oFilter,
      });
      setFilters(newFilters);

      // if this splicer uses oscillators then show them
      setShowOsc(splicer.hasOsc); 

      // update the data values
      let changes = Object.assign({}, data);
      changes.splicerId = newFilterValue.value;
      changes.splicer = splicer.label;
      changes.chamberId = 0;
      changes.chamber = "";
      changes.oscillatorId = 0;
      changes.oscillator = "";
      setData(changes);
   };

   function onChamberChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const cbr = _.find(chambers, (x) => x.value === newFilterValue.value);
      let changes = Object.assign({}, data);
      changes.chamberId = newFilterValue.value;
      changes.chamber = cbr.label;
      setData(changes);
   }

   function onOscillatorChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const ocl = _.find(oscillators, (x) => x.value === newFilterValue.value);
      let changes = Object.assign({}, data);
      changes.oscillatorId = newFilterValue.value;
      changes.oscillator = ocl.label;
      setData(changes);
   }

   const onSelectChange = ((itm) => {
      if (itm === null) {
         setSelectedItem(initialSelect);
         return;
      }
      if (typeof itm?.value !== 'string' || itm?.value.length <= 1)
         toast.error(`${itm} must be a string and length must be greater than one`);
      setSelectedItem(itm)
   })

   function onThreadChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const td = _.find(threads, (x) => x.value === newFilterValue.value);
      let changes = Object.assign({}, data);
      changes.threadId = newFilterValue.value;
      changes.thread = td.label;
      setData(changes);
   }

   const onThreadSpliceChange = async (newFilterValue) => {
      // fill in the thread related drop-down lists with proper values
      const splicer = _.find(thdSplicers, (x) => x.value === newFilterValue.value);
      let newThreadList = await fixThreads(newFilterValue.value);
      const thdFilter = _.find(newThreadList, (t) => t.value === 0);

      // set the related filters
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "thdSplicer",
         value: splicer,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "thread",
         value: thdFilter,
      });
      setFilters(newFilters);

      // update the data values
      let changes = Object.assign({}, data);
      changes.splicerId = newFilterValue.value;
      changes.splicer = splicer.label;
      changes.threadId = 0;
      changes.thread = "";
      setData(changes);
   };

   function saveSpliceItem() {
      let warnings = [];

      if (!data.splicerId > 0) {
         warnings.push("Splicer is required\n");
      }
      if (data.isThread && data.threadId < 1) {
         warnings.push("Thread Type is required\n");
      }
      if (data.isAir && data.chamberId < 1) {
         warnings.push("Chamber is required\n");
      }
      if (data.isAir && showOsc && data.oscillatorId < 1) {
         warnings.push("Oscillator is required\n");
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      }

      api
         .post(`MachineSetup/SaveSpliceRequirements`, data)
         .then((response) => {
            if (response.data.success) {
               toast.success("Splice requirements saved successfully!");
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   return (
      <DesktopContainer screenName="Item Splice Requirements">
         <ServiceThreadSpinner on={!loaded}>
            <div className="filter-header mb-3">
               <Row className="justify-content-center align-items-center" style={{ paddingBottom: "10px" }}>
                  <Col className="width115px col-sm-2 h5">
                     <span>Item:</span>
                  </Col>
                  <Col className="width500px">
                     <ItemTypeahead inline
                        name="itemInput"
                        value={selectedItem}
                        onChange={opt => onSelectChange(opt)}
                        filterItemBy={constants.TYPE_AHEAD_CLASSES.SpecItemsWithAllActiveSamples}
                        includeSampleItems={true}
                     />
                  </Col>
               </Row>
               {data?.joinType ? (
                  <Row className="justify-content-center align-items-center">
                     <Col className="width115px col-sm-2 h5">
                        <span>Join Type:</span>
                     </Col>
                     <Col className="width500px h5">
                        <span>{data?.joinType}</span>
                     </Col>
                  </Row>
               ) : null}
            </div>

            {data?.item && data.item !== "" ? (
               <div>
                  {data?.isThread ? (
                     <Card className="dashboard-card">
                        <CardBody>
                           <div>
                              <span className="h2 me-2">Thread Splicer Requirements</span>
                           </div>
                           <hr />
                           <Row className="justify-content-center align-items-center">
                              <Col className="width115px col-sm-2 h5">
                                 <span>Splicer:</span>
                              </Col>
                              <Col className="width500px">
                                 <FilterSelect
                                    filterName="thdSplicer"
                                    options={thdSplicers}
                                    value={filterHelpers.getValue(filters, "thdSplicer")}
                                    onChangeCallback={(e) => onThreadSpliceChange(e)}
                                    disabled={noEdit}
                                 />
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                           <Row className="justify-content-center align-items-center">
                              <Col className="width115px col-sm-2 h5">
                                 <span>Thread Type:</span>
                              </Col>
                              <Col className="width500px">
                                 <FilterSelect
                                    filterName="thread"
                                    options={threads}
                                    value={filterHelpers.getValue(filters, "thread")}
                                    onChangeCallback={(e) => onThreadChange(e)}
                                    disabled={noEdit}
                                 />
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                           {!noEdit ? (
                              <Row className="justify-content-center align-items-center">
                                 <Col className="width115px col-sm-2 h5">
                                    <span>&nbsp;</span>
                                 </Col>
                                 <Col className="width500px">
                                    <Button
                                       title="Save"
                                       className="shredButton btn-md width265px"
                                       onClick={() => saveSpliceItem()}
                                    >
                                       Save
                                    </Button>
                                 </Col>
                              </Row>
                           ) : null}
                        </CardBody>
                     </Card>
                  ) : null}

                  {data?.isAir ? (
                     <Card className="dashboard-card">
                        <CardBody>
                           <div>
                              <span className="h2 me-2">Air Splicer Requirements</span>
                           </div>
                           <hr />
                           <Row className="justify-content-center align-items-center">
                              <Col className="width115px col-sm-2 h5">
                                 <span>Splicer:</span>
                              </Col>
                              <Col className="width500px">
                                 <FilterSelect
                                    filterName="airSplicer"
                                    options={airSplicers}
                                    value={filterHelpers.getValue(filters, "airSplicer")}
                                    onChangeCallback={(e) => onAirSpliceChange(e)}
                                    disabled={noEdit}
                                 />
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                           <Row className="justify-content-center align-items-center">
                              <Col className="width115px col-sm-2 h5">
                                 <span>Chamber:</span>
                              </Col>
                              <Col className="width500px">
                                 <FilterSelect
                                    filterName="chamber"
                                    options={chambers}
                                    value={filterHelpers.getValue(filters, "chamber")}
                                    onChangeCallback={(e) => onChamberChange(e)}
                                    disabled={noEdit}
                                 />
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                           {showOsc ? (
                              <Row className="justify-content-center align-items-center">
                                 <Col className="width115px col-sm-2 h5">
                                    <span>Oscillator:</span>
                                 </Col>
                                 <Col className="width500px">
                                    <FilterSelect
                                       filterName="oscillator"
                                       options={oscillators}
                                       value={filterHelpers.getValue(filters, "oscillator")}
                                       onChangeCallback={(e) => onOscillatorChange(e)}
                                       disabled={noEdit}
                                    />
                                 </Col>
                              </Row>
                           ) : null}
                           <Row className="lineheight8">&nbsp;</Row>
                           {!noEdit ? (
                              <Row className="justify-content-center align-items-center">
                                 <Col className="width115px col-sm-2 h5">
                                    <span>&nbsp;</span>
                                 </Col>
                                 <Col className="width500px">
                                    <Button
                                       title="Save"
                                       className="shredButton btn-md width265px"
                                       onClick={() => saveSpliceItem()}
                                    >
                                       Save
                                    </Button>
                                 </Col>
                              </Row>
                           ) : null}
                        </CardBody>
                     </Card>
                  ) : null}
               </div>
            ) : null}
            <br />
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
